import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AButton } from "@/components/ios";
import { motion } from "framer-motion";
import { Building2, Loader2 } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";

const DEMO = [
  { email: "principal@harbourcity.com.au", role: "Principal" },
  { email: "pm@harbourcity.com.au", role: "Property Manager" },
  { email: "inspector@harbourcity.com.au", role: "Inspector" },
];

export default function Login() {
  const { login, googleLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("principal@harbourcity.com.au");
  const [password, setPassword] = useState("Passw0rd!");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async (resp) => {
    setErr("");
    try {
      await googleLogin(resp.credential);
      nav("/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "var(--accent)" }}>
        <div className="flex items-center gap-3 text-white">
          <div className="h-10 w-10 rounded-2xl bg-white/20 grid place-items-center"><Building2 size={22} /></div>
          <span className="font-semibold text-lg">Harbour City Property Group</span>
        </div>
        <div className="text-white">
          <h1 className="text-6xl font-bold leading-none tracking-tight">PropPro</h1>
          <p className="mt-4 text-white/80 max-w-md leading-relaxed">
            Evidence-first, human-approved condition reports. Every claim traces to a timestamp, photo,
            and signature — defensible at NCAT, VCAT and QCAT.
          </p>
        </div>
        <div className="text-white/60 text-sm">Data residency: AWS ap-southeast-2 (Sydney)</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <h2 className="text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Sign in</h2>
          <p className="text-sm text-secondary mt-1">Welcome back. Enter your agency credentials.</p>

          <form onSubmit={submit} className="mt-8 space-y-3">
            <div>
              <label className="text-xs font-semibold text-secondary uppercase tracking-wide">Email</label>
              <input
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl px-4 py-3 text-sm outline-none border focus:ring-2"
                style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-secondary uppercase tracking-wide">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-xl px-4 py-3 text-sm outline-none border focus:ring-2"
                style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}
              />
            </div>
            {err && <div className="text-sm" style={{ color: "var(--danger)" }} data-testid="login-error">{err}</div>}
            <AButton testid="login-submit" type="submit" className="w-full !py-3" disabled={loading}>
              {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Sign in"}
            </AButton>
            <button type="button" data-testid="forgot-password" onClick={async () => {
              try { await api.post("/auth/forgot-password", { email }); toast.success("If that email exists, a reset link has been sent (check server logs in demo)."); }
              catch (e2) { toast.error("Please enter a valid email"); }
            }} className="w-full text-center text-sm accent-text font-medium">Forgot password?</button>
          </form>

          <div className="mt-6 flex items-center gap-3" data-testid="google-divider">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            <span className="text-xs text-secondary uppercase tracking-wide">or</span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>

          <div className="mt-6 flex justify-center" data-testid="google-login">
            <GoogleLogin
              onSuccess={onGoogle}
              onError={() => setErr("Google sign-in was cancelled or failed.")}
              theme="outline"
              size="large"
              text="signin_with"
              shape="pill"
              width="320"
            />
          </div>

          <div className="mt-8">
            <div className="text-xs font-semibold text-secondary uppercase tracking-wide mb-2">Demo accounts (password: Passw0rd!)</div>
            <div className="space-y-1.5">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  data-testid={`demo-${d.role.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => { setEmail(d.email); setPassword("Passw0rd!"); }}
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm border transition-colors active:opacity-60"
                  style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}
                >
                  <span className="truncate">{d.email}</span>
                  <span className="text-xs text-secondary shrink-0 ml-2">{d.role}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
